import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { PasscodeObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type PasscodeStoreAction =
  | { type: "SET_PASSCODES"; payload: PasscodeObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_PASSCODES"; payload: PasscodeObj }
  | { type: "DELETE_PASSCODES"; payload: string }
  | { type: "SET_PASSCODES_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_PASSCODES_SAVING_NEW"; payload: boolean };

// Reducer Function
const PasscodeStoreReducer = (
  state: PasscodeStoreState,
  action: PasscodeStoreAction
): PasscodeStoreState => {
  switch (action.type) {
    case "SET_PASSCODES":
      return {
        ...state,
        passcodes: action.payload,
        passcodeLoading: false,
      };

    case "SET_LOADING":
      return { ...state, passcodeLoading: action.payload };

    case "ADD_OR_UPDATE_PASSCODES":
      const index = state.passcodes.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedPasscode = [...state.passcodes];
      if (index > -1) {
        updatedPasscode[index] = action.payload;
      } else {
        updatedPasscode.push(action.payload);
      }
      return { ...state, passcodes: updatedPasscode };

    case "DELETE_PASSCODES":
      return {
        ...state,
        passcodes: state.passcodes.filter((l) => l.id !== action.payload),
      };
    case "SET_PASSCODES_FILES_TO_UPLOAD":
      return { ...state, allergiesFilestoUpload: action.payload };
    case "SET_PASSCODES_SAVING_NEW":
      return { ...state, passcodeSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface PasscodeStoreState {
  passcodes: PasscodeObj[];
  passcodeLoading: boolean;
  passcodeSavingNew: boolean;
  allergiesFilestoUpload: File[];
}

// Hook
export const usePasscodeStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(PasscodeStoreReducer, {
    passcodes: [],
    passcodeLoading: true,
    allergiesFilestoUpload: [],
    passcodeSavingNew: false,
  });

  const deletePasscodeById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchPasscodeData = useV3GenericGetData<PasscodeObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createPasscode = useV3Create<PasscodeObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updatePasscode = useV3Update<PasscodeObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<PasscodeObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handlePasscodeFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.allergiesFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "passcodes",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_PASSCODES",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_PASSCODES_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.allergiesFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadPasscodeDocument = async (
    passcode_id: string,
    document_id: string | null
  ) => {
    if (!document_id || !passcode_id || !ud_id) {
      return Promise.reject("Missing required parameters");
    }
    toast.success("Downloading file...");

    const params: any = {
      document_id: document_id,
      entity: "passcodes",
      entity_id: passcode_id,
      ud_id: ud_id,
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in Passcode objects' documents array
          downloadFileFromS3(
            res,
            state.passcodes
              ?.find((p) => p.id === passcode_id)
              ?.documents?.find((d) => d.document_id === document_id)
              ?.document_name || "Snug_Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document with message "No response"`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
      });
  };

  const handleDeletePasscodeDocument = useCallback(
    async (Passcode_id: string, document_id: string | null): Promise<void> => {
      if (!document_id || !Passcode_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "passcodes",
          entity_id: Passcode_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the Passcode object
          const updatedPasscode = state.passcodes.map((p) => {
            if (p.id === Passcode_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_PASSCODES",
            payload: updatedPasscode,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.passcodes, deleteDocumentById, ud_id]
  );

  const handlePasscodeFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_PASSCODES_FILES_TO_UPLOAD",
        payload: [...state.allergiesFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.allergiesFilestoUpload]
  );

  const handlePasscodeFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.allergiesFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_PASSCODES_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.allergiesFilestoUpload]
  );

  const handlePasscodeFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_PASSCODES_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchPasscodeItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchPasscodeData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "passcodes",
        });

        if (isMounted()) {
          dispatch({ type: "SET_PASSCODES", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchPasscodeData, isMounted]
  );

  const handleAddOrUpdatePasscodeSingleCall = useCallback(
    async (PasscodeItem: PasscodeObj): Promise<PasscodeObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(PasscodeItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updatePasscode(
            {
              ud_id: ud_id,
              entity: "passcodes",
              entity_id: PasscodeItem.id || undefined,
            },
            PasscodeItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createPasscode(
            { ud_id: ud_id, entity: "passcodes" }, // Adjust according to your actual API parameter requirements
            PasscodeItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_PASSCODES", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save  Passcode item.");
        console.error(error);
        return null;
      }
    },
    [ud_id, createPasscode, updatePasscode]
  );

  const handleAddOrUpdatePasscode = useCallback(
    async (PasscodeItem: PasscodeObj) => {
      dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: true });
      if (state.allergiesFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdatePasscodeSingleCall(PasscodeItem);
          if (res) {
            await handlePasscodeFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit  Passcode data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdatePasscodeSingleCall(PasscodeItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit  Passcode data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdatePasscodeSingleCall,
      state.allergiesFilestoUpload,
      handlePasscodeFilesUpload,
    ]
  );

  const handleDeletePasscode = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deletePasscodeById({
          ud_id: ud_id,
          entity: "passcodes",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_PASSCODES", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_PASSCODES_SAVING_NEW", payload: false });
      }
    },
    [deletePasscodeById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchPasscodeItems(ud_id); // Fetch all Passcode items
    }
  }, [ fetchPasscodeItems, ud_id]);

  return {
    passcodes: state.passcodes,
    passcodeLoading: state.passcodeLoading,
    passcodeSavingNew: state.passcodeSavingNew,
    passcodesFilestoUpload: state.allergiesFilestoUpload,
    handleAddOrUpdatePasscode,
    handleDeletePasscode,
    handlePasscodeFileRemove,
    handlePasscodeFilesDrop,
    handlePasscodeFilesRemoveAll,
    handleDownloadPasscodeDocument,
    handleDeletePasscodeDocument,
  };
};
